/**
 * Measurement library (canonical base = meters / square meters).
 *
 * Spatial dimensions are stored in a canonical metric base and converted to the
 * globally-configured display format at the UI boundary. All `format*` functions
 * take canonical values (meters / m²) and all `parse*` functions return canonical
 * values, so storage stays unit-agnostic.
 */

/** Feet → meters. */
export const FT_TO_M = 0.3048;
/** Square feet → square meters. */
export const SQFT_TO_M2 = 0.09290304;
/** Inches → meters. */
export const IN_TO_M = 0.0254;

export type LengthFormat =
  | 'FEET_INCHES'
  | 'DECIMAL_FEET'
  | 'INCHES'
  | 'METERS'
  | 'CENTIMETERS'
  | 'MILLIMETERS';

export type AreaFormat = 'SQUARE_FEET' | 'SQUARE_METERS';

export interface MeasurementSettings {
  lengthFormat: LengthFormat;
  areaFormat: AreaFormat;
}

export const DEFAULT_MEASUREMENT_SETTINGS: MeasurementSettings = {
  lengthFormat: 'FEET_INCHES',
  areaFormat: 'SQUARE_FEET',
};

export const LENGTH_FORMATS: readonly LengthFormat[] = [
  'FEET_INCHES',
  'DECIMAL_FEET',
  'INCHES',
  'METERS',
  'CENTIMETERS',
  'MILLIMETERS',
];

export const AREA_FORMATS: readonly AreaFormat[] = ['SQUARE_FEET', 'SQUARE_METERS'];

export interface ParseOptions {
  /** Allow negative results (e.g. basement floor elevations). Defaults to false. */
  allowNegative?: boolean;
}

/** Round to `decimals` places and drop trailing zeros. */
function trimNumber(value: number, decimals: number): string {
  const rounded = Number.parseFloat(value.toFixed(decimals));
  // Normalize -0 to 0.
  return (Object.is(rounded, -0) ? 0 : rounded).toString();
}

function isBlank(value: number | null | undefined): value is null | undefined {
  return value === null || value === undefined || Number.isNaN(value);
}

const LENGTH_DECIMALS: Record<Exclude<LengthFormat, 'FEET_INCHES'>, number> = {
  DECIMAL_FEET: 2,
  INCHES: 1,
  METERS: 3,
  CENTIMETERS: 1,
  MILLIMETERS: 0,
};

/**
 * Format a canonical length (meters) for display in the given format.
 * Returns `''` for null/undefined (render blank, never `0`).
 */
export function formatLength(meters: number | null | undefined, fmt: LengthFormat): string {
  if (isBlank(meters)) return '';

  if (fmt === 'FEET_INCHES') {
    const totalInches = Math.round(meters / IN_TO_M);
    const sign = totalInches < 0 ? '-' : '';
    const abs = Math.abs(totalInches);
    const feet = Math.floor(abs / 12);
    const inches = abs % 12;
    return `${sign}${feet}' ${inches}"`;
  }

  const decimals = LENGTH_DECIMALS[fmt];
  switch (fmt) {
    case 'DECIMAL_FEET':
      return trimNumber(meters / FT_TO_M, decimals);
    case 'INCHES':
      return trimNumber(meters / IN_TO_M, decimals);
    case 'METERS':
      return trimNumber(meters, decimals);
    case 'CENTIMETERS':
      return trimNumber(meters * 100, decimals);
    case 'MILLIMETERS':
      return trimNumber(meters * 1000, decimals);
    default:
      return '';
  }
}

/**
 * Parse a length string in the given format to canonical meters.
 * Returns `null` for empty/garbage input. Rejects negatives unless
 * `options.allowNegative` is set.
 */
export function parseLength(
  text: string,
  fmt: LengthFormat,
  options: ParseOptions = {}
): number | null {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (trimmed === '') return null;

  let meters: number | null;
  if (fmt === 'FEET_INCHES') {
    meters = parseFeetInches(trimmed);
  } else {
    const value = parsePlainNumber(trimmed);
    if (value === null) return null;
    switch (fmt) {
      case 'DECIMAL_FEET':
        meters = value * FT_TO_M;
        break;
      case 'INCHES':
        meters = value * IN_TO_M;
        break;
      case 'METERS':
        meters = value;
        break;
      case 'CENTIMETERS':
        meters = value / 100;
        break;
      case 'MILLIMETERS':
        meters = value / 1000;
        break;
      default:
        meters = null;
    }
  }

  if (meters === null || Number.isNaN(meters)) return null;
  if (!options.allowNegative && meters < 0) return null;
  return meters;
}

/**
 * Parse feet-and-inches syntax to canonical meters. Accepts:
 * `12' 6"`, `12'6"`, `12 6`, `12`, `6"`, and plain decimals (`12.5` = feet).
 */
function parseFeetInches(text: string): number | null {
  const negative = text.startsWith('-');
  const body = negative ? text.slice(1).trim() : text;

  let feet: number | null = null;
  let inches: number | null = null;

  const hasFeetMark = body.includes("'");
  const hasInchMark = body.includes('"');

  if (hasFeetMark || hasInchMark) {
    const feetMatch = body.match(/(-?\d+(?:\.\d+)?)\s*'/);
    const inchMatch = body.match(/(-?\d+(?:\.\d+)?)\s*"/);
    if (feetMatch) feet = Number.parseFloat(feetMatch[1]);
    if (inchMatch) inches = Number.parseFloat(inchMatch[1]);
    if (feet === null && inches === null) return null;
  } else {
    const parts = body.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      feet = parsePlainNumber(parts[0]);
      inches = parsePlainNumber(parts[1]);
    } else if (parts.length === 1) {
      // Single plain value is interpreted as decimal feet.
      feet = parsePlainNumber(parts[0]);
    }
  }

  if (feet === null && inches === null) return null;
  const totalFeet = (feet ?? 0) + (inches ?? 0) / 12;
  const meters = totalFeet * FT_TO_M;
  return negative ? -meters : meters;
}

/** Parse a strict numeric string (allows leading sign and decimals). */
function parsePlainNumber(text: string): number | null {
  const trimmed = text.trim();
  if (!/^[-+]?\d*\.?\d+$/.test(trimmed)) return null;
  const value = Number.parseFloat(trimmed);
  return Number.isNaN(value) ? null : value;
}

/**
 * Format a canonical area (m²) for display in the given format.
 * Returns `''` for null/undefined.
 */
export function formatArea(m2: number | null | undefined, fmt: AreaFormat): string {
  if (isBlank(m2)) return '';
  switch (fmt) {
    case 'SQUARE_FEET':
      return trimNumber(m2 / SQFT_TO_M2, 2);
    case 'SQUARE_METERS':
      return trimNumber(m2, 2);
    default:
      return '';
  }
}

/**
 * Parse an area string in the given format to canonical m².
 * Returns `null` for empty/garbage input. Always rejects negatives.
 */
export function parseArea(text: string, fmt: AreaFormat): number | null {
  if (typeof text !== 'string') return null;
  const value = parsePlainNumber(text.trim());
  if (value === null || value < 0) return null;
  switch (fmt) {
    case 'SQUARE_FEET':
      return value * SQFT_TO_M2;
    case 'SQUARE_METERS':
      return value;
    default:
      return null;
  }
}

/** Short unit label for a length format (for field captions / table headers). */
export function lengthUnitLabel(fmt: LengthFormat): string {
  switch (fmt) {
    case 'FEET_INCHES':
      return 'ft-in';
    case 'DECIMAL_FEET':
      return 'ft';
    case 'INCHES':
      return 'in';
    case 'METERS':
      return 'm';
    case 'CENTIMETERS':
      return 'cm';
    case 'MILLIMETERS':
      return 'mm';
    default:
      return '';
  }
}

/** Short unit label for an area format. */
export function areaUnitLabel(fmt: AreaFormat): string {
  switch (fmt) {
    case 'SQUARE_FEET':
      return 'sq ft';
    case 'SQUARE_METERS':
      return 'm\u00B2';
    default:
      return '';
  }
}
